import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft } from 'lucide-react';
import BookingDetails from '@/components/payment/BookingDetails';
import PaymentSummary from '@/components/payment/PaymentSummary';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { BookingInfo } from '@/types/booking';
import { useNewPaymentFlow } from '@/hooks/useNewPaymentFlow';
import { supabase } from '@/integrations/supabase/client';
import TimezoneWarningModal from '@/components/booking/TimezoneWarningModal';
import { getUserTimezone } from '@/utils/timezone-helpers';
import { useSlotReservation } from '@/hooks/useSlotReservation';
import { STANDARD_SESSION_FORMATS } from '@/utils/session-format-helpers';
import { useToast } from '@/hooks/use-toast';

const Payment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [discountCode, setDiscountCode] = useState<string>("");
  const [discount, setDiscount] = useState<number>(0);
  const [couponId, setCouponId] = useState<string | null>(null);
  
  const { 
    isProcessing, 
    initializePayment, 
    handlePaymentError 
  } = useNewPaymentFlow();
  
  const { isReserving, reservationExpiry, reserveSlot, clearReservation } = useSlotReservation();

  // Timezone warning states
  const [showTimezoneWarning, setShowTimezoneWarning] = useState(false);
  const [clientTimezone, setClientTimezone] = useState<string | null>(null);
  const [practitionerTimezone, setPractitionerTimezone] = useState<string>('America/New_York');
  const [pendingPayment, setPendingPayment] = useState(false);
  const [reservedBookingId, setReservedBookingId] = useState<string | null>(null);

  // Handle canceled checkout
  useEffect(() => {
    const canceled = searchParams.get('canceled');
    if (canceled === 'true') {
      toast({
        variant: "destructive",
        title: "Payment Canceled",
        description: "Your payment was canceled. You can try again."
      });
      // Clear the canceled param from URL
      navigate('/payment', { replace: true });
    }
  }, [searchParams, toast, navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reserve slot when booking info is loaded
  useEffect(() => {
    const reserveSlotOnLoad = async () => {
      if (!bookingInfo || !bookingInfo.selectedDate || !bookingInfo.selectedTime || reservedBookingId) {
        return;
      }

      console.log("Reserving slot for booking:", bookingInfo);
      const selectedDate = new Date(bookingInfo.selectedDate);

      const bookingId = await reserveSlot(bookingInfo, selectedDate, bookingInfo.selectedTime);

      if (bookingId) {
        setReservedBookingId(bookingId);
        // Store in booking info for payment processing
        setBookingInfo(prev => prev ? { ...prev, bookingId } : prev);
      }
    };

    reserveSlotOnLoad();
  }, [bookingInfo?.practitionerId, bookingInfo?.serviceId, bookingInfo?.selectedDate, bookingInfo?.selectedTime]);

  // Release slot when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (reservedBookingId && !isProcessing) {
        // Show warning but don't delete booking
        // The booking will auto-expire after 10 minutes on the server
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [reservedBookingId, isProcessing]);

  useEffect(() => {
    console.log("Payment page: Loading booking info from sessionStorage");
    const storedBookingInfo = sessionStorage.getItem('bookingInfo');
    console.log("Raw stored booking info:", storedBookingInfo);

    if (storedBookingInfo) {
      try {
        const parsedBookingInfo: BookingInfo = JSON.parse(storedBookingInfo);
        console.log("Parsed booking info:", parsedBookingInfo);
        
        // Validate required fields
        if (!parsedBookingInfo.practitionerId || !parsedBookingInfo.serviceId) {
          throw new Error("Missing required booking IDs");
        }
        
        if (!parsedBookingInfo.selectedDate || !parsedBookingInfo.selectedTime) {
          throw new Error("Missing selected date or time");
        }
        
        setBookingInfo(parsedBookingInfo);
        setIsLoading(false);
      } catch (parseError) {
        console.error("Error parsing booking info from session storage:", parseError);
        setError(t('invalidBookingInfo'));
        setIsLoading(false);
        return;
      }
    } else {
      console.error("No booking information found in sessionStorage");
      
      // Check if there's tempBookingInfo (user came directly to payment without going through datetime)
      const tempBookingInfo = sessionStorage.getItem('tempBookingInfo');
      if (tempBookingInfo) {
        console.log("Found tempBookingInfo, redirecting to datetime selection");
        navigate('/booking-datetime');
        return;
      }
      
      setError(t('noBookingInfo'));
      setIsLoading(false);
    }
  }, [navigate, t]);

  // Load timezone information when booking info is available
  useEffect(() => {
    const loadTimezoneInfo = async () => {
      if (!user || !bookingInfo) return;

      try {
        // Get client timezone
        const userTimezone = await getUserTimezone(user.id);
        setClientTimezone(userTimezone);

        // Get practitioner timezone
        if (bookingInfo.practitionerId) {
          const practitionerTimezone = await getUserTimezone(bookingInfo.practitionerId);
          setPractitionerTimezone(practitionerTimezone);
        }
      } catch (error) {
        console.error('Error loading timezone info:', error);
        // Use America/New_York as fallback
        setClientTimezone('America/New_York');
      }
    };

    loadTimezoneInfo();
  }, [user, bookingInfo]);

  // Auto-apply referral discount if user is eligible
  useEffect(() => {
    const checkReferralDiscount = async () => {
      if (!user) return;

      try {
        const userType = user.user_metadata?.user_type || 'client';
        const tableName = userType === 'client' ? 'clients' : 'practitioners';

        const { data: profile, error } = await supabase
          .from(tableName as any)
          .select('referral_discount_eligible, referral_discount_used')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking referral discount eligibility:', error);
          return;
        }

        // If user is eligible and hasn't used the discount yet
        const profileData = profile as any;
        if (profileData?.referral_discount_eligible && !profileData?.referral_discount_used) {
          console.log('User is eligible for referral discount, auto-applying RESONOW10');
          setDiscountCode('RESONOW10');
          // Note: We'll validate and apply it when component is ready
        }
      } catch (err) {
        console.error('Exception checking referral discount:', err);
      }
    };

    checkReferralDiscount();
  }, [user]);

  const handlePaymentSubmit = async () => {
    if (!bookingInfo || !bookingInfo.selectedDate || !bookingInfo.selectedTime || !reservedBookingId) {
      setError(t('missingBookingInfo'));
      return;
    }

    // Check timezone warning conditions
    const isTimezoneNotSet = !clientTimezone;
    const isDifferentTimezone = clientTimezone && clientTimezone !== practitionerTimezone;

    if ((isTimezoneNotSet || isDifferentTimezone) && !pendingPayment) {
      setShowTimezoneWarning(true);
      setPendingPayment(true);
      return;
    }

    console.log("Starting payment with booking info:", bookingInfo);

    // For free bookings ($0), set as Reserved/Free to require practitioner confirmation
    if (bookingInfo.price === 0) {
      try {
        console.log("Free booking detected, setting to Reserved/Free for practitioner confirmation:", reservedBookingId);

        // Calculate confirmation deadline (48 hours from now)
        const confirmationDeadline = new Date();
        confirmationDeadline.setHours(confirmationDeadline.getHours() + 48);

        const { error: updateError } = await supabase
          .from('bookings')
          .update({
            payment_status: 'Free',
            status: 'Reserved',
            confirmation_deadline: confirmationDeadline.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', reservedBookingId);

        if (updateError) {
          console.error("Error updating free booking:", updateError);
          setError("Failed to confirm booking. Please try again.");
          return;
        }

        // Send booking pending confirmation email (practitioner needs to confirm)
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              template_type: 'bookingPendingConfirmation',
              booking_id: reservedBookingId,
              client_id: user?.id,
              practitioner_id: bookingInfo.practitionerId
            }
          });
        } catch (emailError) {
          console.error("Failed to send pending confirmation emails:", emailError);
        }

        navigate(`/booking-success?booking_id=${reservedBookingId}`);
        return;
      } catch (error: any) {
        console.error("Free booking error:", error);
        setError(error.message || "Failed to confirm booking. Please try again.");
        return;
      }
    }

    // For paid bookings, redirect to Stripe Checkout
    try {
      const selectedDate = new Date(bookingInfo.selectedDate);
      await initializePayment(
        reservedBookingId, 
        bookingInfo, 
        selectedDate,
        couponId,  // Pass validated coupon code
        discount   // Pass discount percentage
      );
      // initializePayment will redirect to Stripe Checkout
    } catch (error: any) {
      console.error("Payment error:", error);
      setError(error.message || "Payment failed. Please try again.");
      setPendingPayment(false);
    }
  };

  const handleTimezoneWarningConfirm = () => {
    setShowTimezoneWarning(false);
    // Continue with payment after timezone warning is acknowledged
    handlePaymentSubmit();
  };

  // Calculate days until session for UI messaging
  const getDaysUntilSession = (): number => {
    if (!bookingInfo?.selectedDate) return 0;
    const selectedDate = new Date(bookingInfo.selectedDate);
    return Math.ceil((selectedDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const getPaymentModeMessage = (): string => {
    const days = getDaysUntilSession();
    if (days > 3) {
      return "Your card will be saved securely. Payment will be authorized 3 days before your session.";
    } else {
      return "Your payment will be pre-authorized now. Funds will be captured 2 days after your session completes.";
    }
  };

  const handleApplyDiscount = () => {
    // This function will be called by PaymentSummary when a valid coupon is applied
    console.log("Discount applied:", { discountCode, discount, couponId });
  };

  const handleBack = () => {
    try {
      const temp = sessionStorage.getItem('tempBookingInfo');
      if (temp) {
        const parsed = JSON.parse(temp);
        if (parsed.returnToPath) {
          navigate(parsed.returnToPath);
          return;
        }
        if (parsed.practitionerId) {
          navigate(`/book-an-appointment/${parsed.practitionerId}`);
          return;
        }
      }
      if (bookingInfo?.practitionerId) {
        navigate(`/book-an-appointment/${bookingInfo.practitionerId}`);
      } else {
        navigate('/book-an-appointment');
      }
    } catch {
      navigate('/book-an-appointment');
    }
  };
  // Transform BookingInfo to match BookingDetails expected format
  const transformBookingInfo = (info: BookingInfo) => {
    // Parse the actual selected date and time from booking info
    let actualDateTime = new Date();
    
    if (info.selectedDate && info.selectedTime) {
      try {
        const selectedDate = new Date(info.selectedDate);
        const [hours, minutes] = info.selectedTime.split(':').map(Number);
        selectedDate.setHours(hours, minutes, 0, 0);
        actualDateTime = selectedDate;
      } catch (e) {
        console.error("Error parsing date/time:", e);
        // Fallback to tomorrow if parsing fails
        actualDateTime.setHours(actualDateTime.getHours() + 24);
      }
    } else {
      // Fallback to mock date if no actual date/time
      actualDateTime.setHours(actualDateTime.getHours() + 24);
    }
    
    return {
      practitioner: {
        first_name: info.practitionerName.split(' ')[0] || 'Unknown',
        last_name: info.practitionerName.split(' ').slice(1).join(' ') || 'Practitioner',
        professional_title: 'Wellness Practitioner',
        avatar_url: undefined
      },
      service: {
        service_name: info.service,
        duration_minutes: info.duration,
        price: info.price,
        currency: info.currency
      },
      dateTime: actualDateTime,
      format: (info.format || STANDARD_SESSION_FORMATS.VIRTUAL) as 'virtual' | 'in_person' | 'online',
      location: undefined,
      notes: undefined,
      practitionerTimezone: info.practitionerTimezone || practitionerTimezone || 'America/New_York'
    };
  };

  const isFormComplete = bookingInfo?.selectedDate && bookingInfo?.selectedTime;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <Button 
              variant="ghost" 
              onClick={handleBack}
              className="mb-6"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('back')}
            </Button>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!bookingInfo) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <Alert>
              <AlertDescription>
                {t('noBookingInfo')}
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
        <div className="container mx-auto max-w-6xl">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-6"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>

          <div className="grid gap-8 lg:grid-cols-2">
            <div className="lg:col-span-1 space-y-6">
              <PaymentSummary
                price={bookingInfo.price}
                currency={bookingInfo.currency}
                discount={discount}
                discountCode={discountCode}
                onDiscountCodeChange={setDiscountCode}
                onApplyDiscount={handleApplyDiscount}
                onPaymentSubmit={handlePaymentSubmit}
                isLoading={isProcessing || isReserving}
                stripeLoaded={true}
                isUserLoggedIn={!!user}
                isFormComplete={!!isFormComplete}
                onSetCouponId={setCouponId}
                reservationExpiry={reservationExpiry}
              />
              
              {/* Payment mode info */}
              {getDaysUntilSession() > 0 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800">
                    {getPaymentModeMessage()}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="lg:col-span-1">
              <BookingDetails 
                bookingInfo={transformBookingInfo(bookingInfo)}
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      {/* Timezone Warning Modal */}
      {bookingInfo && (
        <TimezoneWarningModal
          open={showTimezoneWarning}
          onConfirm={handleTimezoneWarningConfirm}
          clientTimezone={clientTimezone}
          practitionerTimezone={practitionerTimezone}
          appointmentTime={new Date(`${bookingInfo.selectedDate} ${bookingInfo.selectedTime}`)}
          practitionerName={bookingInfo.practitionerName}
        />
      )}
    </div>
  );
};

export default Payment;
